import React from 'react';
import { Link, Image, useRouter } from './common/NextAdapters';
import { Icon } from '@iconify/react';
import { cn } from '../lib/utils';
import { movieApi } from '../api/client';

function useDebounce(value, ms = 300) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function normalizeMovie(m) {
  if (!m) return null;
  return {
    id: m.id || m.Id || m.MovieId,
    title: m.title || m.Title || m.Name || 'Untitled',
    thumbnail: m.thumbnail || m.ThumbnailUrl || m.CoverUrl || m.BannerUrl,
    category: m.category || m.CategoryName || m.Genre,
    year: m.year || m.ReleaseYear || '',
    isSeries: m.isSeries ?? m.IsSeries ?? (m.Type === 'Series'),
    contentType: m.contentType || m.ContentType || (m.IsSeries ? 'Series' : 'Movie'),
  };
}

const PER_GROUP = 5;

function groupByCategory(results) {
  const map = new Map();
  for (const title of results) {
    const genre = title.category || 'Featured';
    if (!map.has(genre)) map.set(genre, []);
    map.get(genre).push(title);
  }
  return [...map.entries()].map(([name, titles]) => ({
    name,
    titles: titles.slice(0, PER_GROUP),
  }));
}

function ResultItem({ title, active, onSelect }) {
  return (
    <Link
      href={`/movie/${title.id}`}
      onClick={onSelect}
      data-active={active || undefined}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors',
        'outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'bg-white/10' : 'hover:bg-white/5'
      )}
    >
      <div className="relative h-12 w-20 shrink-0 overflow-hidden rounded-md bg-card ring-1 ring-white/10">
        {title.thumbnail ? (
          <Image
            src={title.thumbnail}
            alt={title.title}
            fill
            sizes="80px"
            className="object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-white/5">
            <Icon icon="ph:film-slate" className="size-5 text-muted-foreground" />
          </div>
        )}
        {title.isSeries && (
          <span className="absolute top-0.5 left-0.5 rounded-sm bg-black/70 px-1 py-px text-[9px] font-semibold text-white">
            Series
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{title.title}</p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {[title.year, title.category, title.contentType].filter(Boolean).join(' · ')}
        </p>
      </div>
    </Link>
  );
}

function CategoryChip({ category, onSelect }) {
  const slug = category.slug || category.Id || category.id;
  const name = category.name || category.Name;
  return (
    <Link
      href={`/browse?category=${slug}`}
      onClick={onSelect}
      className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs text-muted-foreground transition-colors hover:border-white/25 hover:bg-white/10 hover:text-foreground"
    >
      <Icon icon="ph:tag-bold" className="size-3.5 shrink-0 opacity-60" />
      {name}
    </Link>
  );
}

export function SearchModal({ open, onClose, categories = [], categoryId }) {
  if (!open) return null;
  return <SearchDialog onClose={onClose} categories={categories} categoryId={categoryId} />;
}

function SearchDialog({ onClose, categories = [], categoryId }) {
  const router = useRouter();
  const inputRef = React.useRef(null);
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [fetchedCats, setFetchedCats] = React.useState([]);
  const debouncedQuery = useDebounce(query, 300);

  const cats = categories.length > 0 ? categories : fetchedCats;

  React.useEffect(() => {
    if (categories.length === 0) {
      movieApi
        .getCategories()
        .then((res) => {
          const list = res.data?.data || res.data || [];
          if (Array.isArray(list)) setFetchedCats(list);
        })
        .catch(() => {});
    }
  }, [categories.length]);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  React.useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    movieApi
      .search(debouncedQuery, categoryId)
      .then((res) => {
        if (!cancelled) {
          const raw = res.data?.data || res.data || [];
          const normalized = (Array.isArray(raw) ? raw : []).map(normalizeMovie).filter(Boolean);
          setResults(normalized);
          setActiveIndex(-1);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResults([]);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, categoryId]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const groups = React.useMemo(() => groupByCategory(results), [results]);
  const flatItems = React.useMemo(() => groups.flatMap((g) => g.titles), [groups]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1 < flatItems.length ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 >= 0 ? prev - 1 : flatItems.length - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && flatItems[activeIndex]) {
        e.preventDefault();
        onClose();
        router.push(`/movie/${flatItems[activeIndex].id}`);
      } else if (query.trim()) {
        e.preventDefault();
        onClose();
        router.push(`/browse?q=${encodeURIComponent(query.trim())}`);
      }
    }
  };

  let globalIndex = -1;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-6 md:p-20">
      <div
        className="fixed inset-0 bg-black/75 backdrop-blur-md transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search Kast"
        className="relative z-10 w-full max-w-2xl overflow-hidden rounded-2xl border border-white/10 bg-[#110e1b] shadow-2xl ring-1 ring-white/10 transition-all"
        onKeyDown={handleKeyDown}
      >
        {/* Search Input Bar */}
        <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3.5">
          <Icon icon="ph:magnifying-glass-bold" className="size-5 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search movies, series, genres, creators..."
            className="flex-1 bg-transparent text-base text-foreground placeholder:text-muted-foreground outline-none"
          />
          {loading && (
            <Icon icon="ph:spinner" className="size-5 animate-spin text-muted-foreground" />
          )}
          {query && !loading && (
            <button
              onClick={() => setQuery('')}
              className="rounded-full p-1 text-muted-foreground hover:bg-white/10 hover:text-foreground"
            >
              <Icon icon="ph:x-bold" className="size-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded-md px-2 py-1 text-xs font-semibold text-muted-foreground hover:bg-white/10 hover:text-foreground"
          >
            ESC
          </button>
        </div>

        {/* Results / Categories */}
        <div className="max-h-[60vh] overflow-y-auto p-4">
          {query.length < 2 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Popular Genres
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {cats.slice(0, 10).map((cat) => (
                  <CategoryChip
                    key={cat.id || cat.Id || cat.slug || cat.name}
                    category={cat}
                    onSelect={onClose}
                  />
                ))}
              </div>
            </div>
          )}

          {query.length >= 2 && results.length === 0 && !loading && (
            <div className="py-12 text-center">
              <Icon icon="ph:film-slash" className="mx-auto size-10 text-muted-foreground/60" />
              <p className="mt-3 text-sm text-muted-foreground">
                No titles found for &ldquo;{query}&rdquo;
              </p>
            </div>
          )}

          {results.length > 0 && (
            <div className="space-y-6">
              {groups.map((group) => (
                <div key={group.name}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {group.name}
                  </p>
                  <div className="mt-2 space-y-1">
                    {group.titles.map((title) => {
                      globalIndex++;
                      const isSelected = globalIndex === activeIndex;
                      return (
                        <ResultItem
                          key={title.id}
                          title={title}
                          active={isSelected}
                          onSelect={onClose}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
